/**
 * Address Service
 * Handles user address management for checkout and profile
 */

import authService from './authService.js';
import { frontendLogger } from '../../services/frontendLogger.js';

class AddressService {
  
  /**
   * Get all user addresses
   */
  async getUserAddresses() {
    try {
      const response = await authService.request('/addresses');
      return response;
    } catch (error) {
      frontendLogger.error('Error fetching user addresses', {
        error: error.message || 'Unknown error',
        context: {
          service: 'AddressService',
          operation: 'getUserAddresses'
        }
      });
      throw error;
    }
  }

  /**
   * Add a new address
   */
  async addAddress(addressData) {
    try {
      const response = await authService.request('/addresses', {
        method: 'POST',
        body: JSON.stringify(addressData)
      });
      return response;
    } catch (error) {
      frontendLogger.error('Error adding address', {
        error: error.message || 'Unknown error',
        context: {
          service: 'AddressService',
          operation: 'addAddress',
          hasData: !!addressData
        }
      });
      throw error;
    }
  }

  /**
   * Update an existing address
   */
  async updateAddress(addressId, addressData) {
    try {
      const response = await authService.request(`/addresses/${addressId}`, {
        method: 'PUT',
        body: JSON.stringify(addressData)
      });
      return response;
    } catch (error) {
      frontendLogger.error('Error updating address', {
        error: error.message || 'Unknown error',
        context: {
          service: 'AddressService',
          operation: 'updateAddress',
          addressId,
          hasData: !!addressData
        }
      });
      throw error;
    }
  }

  /**
   * Delete an address
   */
  async deleteAddress(addressId) {
    try {
      const response = await authService.request(`/addresses/${addressId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      frontendLogger.error('Error deleting address', {
        error: error.message || 'Unknown error',
        context: {
          service: 'AddressService',
          operation: 'deleteAddress',
          addressId
        }
      });
      throw error;
    }
  }

  /**
   * Set an address as the main address
   */
  async setMainAddress(addressId) {
    try {
      const response = await authService.request(`/addresses/${addressId}/set-main`, {
        method: 'POST'
      });
      return response;
    } catch (error) {
      frontendLogger.error('Error setting main address', {
        error: error.message || 'Unknown error',
        context: {
          service: 'AddressService',
          operation: 'setMainAddress',
          addressId
        }
      });
      throw error;
    }
  }

  /**
   * Validate address data
   */
  validateAddress(addressData) {
    const errors = [];
    
    if (!addressData.full_name?.trim()) {
      errors.push('Full name is required');
    }
    
    if (!addressData.street_address?.trim()) {
      errors.push('Street address is required');
    }
    
    if (!addressData.city?.trim()) {
      errors.push('City is required');
    }
    
    if (!addressData.state?.trim()) {
      errors.push('State is required');
    }
    
    if (!addressData.postal_code?.trim()) {
      errors.push('Postal code is required');
    }
    
    if (!addressData.country?.trim()) {
      errors.push('Country is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Format address for display
   */
  formatAddressDisplay(address) {
    if (!address) return '';
    
    const parts = [];
    
    if (address.street_address) {
      parts.push(address.street_address);
    }
    
    if (address.apartment) {
      parts.push(address.apartment);
    }
    
    const cityStateParts = [];
    if (address.city) cityStateParts.push(address.city);
    if (address.state) cityStateParts.push(address.state);
    if (address.postal_code) cityStateParts.push(address.postal_code);
    
    if (cityStateParts.length > 0) {
      parts.push(cityStateParts.join(', '));
    }
    
    if (address.country && address.country !== 'US') {
      parts.push(address.country);
    }
    
    return parts.join('\n');
  }

  /**
   * Get address summary (one line)
   */
  formatAddressSummary(address) {
    if (!address) return '';
    
    const parts = [];
    
    if (address.street_address) {
      parts.push(address.street_address);
    }
    
    if (address.city) {
      parts.push(address.city);
    }
    
    if (address.state) {
      parts.push(address.state);
    }
    
    if (address.postal_code) {
      parts.push(address.postal_code);
    }
    
    return parts.join(', ');
  }
}

const addressService = new AddressService();
export default addressService;